import React, { useId, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { FiChevronDown } from 'react-icons/fi';

import CONDITION_THUMBNAILS from '../assets/conditions';

/**
 * One condition, expandable to show what it tends to look like.
 *
 * A disclosure, not a link: the arrow toggles the panel rather than navigating,
 * so it carries `aria-expanded` / `aria-controls` and the whole header is the
 * hit area.
 */
function CategoryCard({ category, open, onToggle }) {
  const panelId = `${useId()}-panel`;
  const thumbnail = CONDITION_THUMBNAILS[category.id];

  return (
    <div className={`mm-category ${open ? 'mm-category--open' : ''}`}>
      <button
        type="button"
        className="mm-category__head"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        {/* Decorative: the condition name sits right beside it, so announcing
            the image too would just repeat. A category without artwork simply
            renders without one. */}
        {thumbnail && (
          <img
            className="mm-category__thumb"
            src={thumbnail}
            alt=""
            width={52}
            height={52}
            loading="lazy"
          />
        )}
        <span className="mm-category__name">{category.name}</span>
        <span className="mm-category__arrow" aria-hidden="true">
          <FiChevronDown size={16} />
        </span>
      </button>

      {/* Kept in the DOM and collapsed with a 0fr grid row so the height can
          animate to content. `visibility` follows the state so assistive tech
          skips the panel while it is closed. */}
      <div className="mm-category__panel" id={panelId} role="region">
        <div className="mm-category__panel-inner">
          <p className="mm-category__symptoms">{category.symptoms}</p>
        </div>
      </div>
    </div>
  );
}

export default function CategoryGrid({ categories = [], loading }) {
  // A Set rather than a single id: this is a browse grid, and someone weighing
  // two conditions against each other should be able to see both at once.
  const [openIds, setOpenIds] = useState(() => new Set());

  const toggle = (id) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <section className="mm-section" id="categories">
      <Container>
        <div className="text-center mb-5">
          <div className="mm-eyebrow mb-2">Browse by condition</div>
          <h2 className="mm-h2">Browse by what&rsquo;s on your mind</h2>
          <p className="mm-lead mt-3 mx-auto" style={{ maxWidth: '52ch' }}>
            You don&rsquo;t need a diagnosis to start. Open any of these to see
            what it often looks like, and pick whatever comes closest.
          </p>
        </div>

        {/* align-items-start so an expanded card grows on its own instead of
            stretching every sibling in its row to match. */}
        <Row className="g-3 align-items-start">
          {(loading
            ? Array.from({ length: 12 }, (_, i) => ({ id: `s-${i}` }))
            : categories
          ).map((category) => (
            <Col key={category.id} xs={12} sm={6} lg={4} xl={3}>
              {loading ? (
                <div className="mm-skeleton" style={{ height: 64 }}>
                  <div className="mm-skeleton__bar" style={{ width: '65%' }} />
                </div>
              ) : (
                <CategoryCard
                  category={category}
                  open={openIds.has(category.id)}
                  onToggle={() => toggle(category.id)}
                />
              )}
            </Col>
          ))}
        </Row>

        <p className="mm-category__disclaimer">
          These are common experiences, not a diagnosis. A therapist can help you
          make sense of what&rsquo;s going on.
        </p>
      </Container>
    </section>
  );
}
